/** External Signals Sources API.
 *
 * The server decides which signals this account may see (its own country plus
 * global series, never a source still waiting for a licence) and how healthy
 * each one is. This layer only fetches; it must not re-derive either.
 */

export type SignalState =
  | 'ok'
  | 'delayed'
  | 'failed'
  | 'empty'
  | 'discontinued';

export interface SignalStatus {
  state: SignalState;
  label: string;
  detail: string | null;
}

export interface Point {
  period: string;
  value: number;
}

export interface Signal {
  key: string;
  name: string;
  family: string;
  scope: 'country' | 'global';
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual' | string;
  unit: string | null;
  source: string;
  source_url: string | null;
  attribution: string | null;
  description: string | null;
  used_for: string | null;
  latest: Point | null;
  previous: Point | null;
  year_ago: number | null;
  change_pct: number | null;
  yoy_pct: number | null;
  coverage_from: string | null;
  points: number;
  updated_at: string | null;
  status: SignalStatus;
  spark: number[];
  featured: boolean;
}

export interface WeatherDay {
  day: string;
  basis: 'observed' | 'forecast' | 'normal';
  tmax: number | null;
  tmin: number | null;
  normal_tmax: number | null;
}

export interface Weather {
  city: string;
  days: WeatherDay[];
  latest_observed: WeatherDay | null;
  forecast_days: number;
  forecast_issued_at: string | null;
  source: string;
  attribution: string;
  used_for: string;
  status: SignalStatus;
  updated_at: string | null;
}

export interface CalendarEvent {
  type: string;
  name: string;
  start: string;
  end: string;
  status: 'confirmed' | 'expected';
  hijri: string;
}

export interface CalendarBlock {
  upcoming: CalendarEvent[];
  events: number;
  coverage_from: string | null;
  source: string;
  used_for: string;
  status: SignalStatus;
  updated_at: string | null;
}

export interface MarketEvent {
  type: string;
  title: string;
  date: string;
  scope: 'country' | 'global';
  before: number | null;
  after: number | null;
  unit: string | null;
  source: string;
  source_url: string | null;
  derived: boolean;
}

export interface Family {
  key: string;
  label: string;
  blurb: string;
}

export interface ExternalSignals {
  country: { code: string; name: string };
  generated_at: string;
  families: Family[];
  signals: Signal[];
  weather: Weather | null;
  calendar: CalendarBlock;
  events: MarketEvent[];
}

export interface SeriesHistory {
  key: string;
  years: number;
  points: [string, number][];
  recent: { period: string; value: number; provisional: boolean }[];
}

async function jsonFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export function apiExternalSignals(): Promise<ExternalSignals> {
  return jsonFetch('/api/external-signals');
}

export function apiSeriesHistory(
  key: string,
  years: number
): Promise<SeriesHistory> {
  return jsonFetch(
    `/api/external-signals/series/${encodeURIComponent(key)}?years=${years}`
  );
}
